<template>
  <div v-if="loading || infos.length > 0">
    <b-row>
      <template v-if="loading">
        <b-col>
          <b-spinner
            small
            variant="primary"
            type="grow"
          />
        </b-col>
        <b-col>
          <b-spinner
            small
            variant="primary"
            type="grow"
          />
        </b-col>
        <b-col>
          <b-spinner
            small
            variant="primary"
            type="grow"
          />
        </b-col>
        <b-col>
          <b-spinner
            small
            variant="primary"
            type="grow"
          />
        </b-col>
      </template>
      <b-col
        v-else
        v-for="(stat, index) in infos"
        :key="index"
      >
        <connector-info-card
          :title="stat.label"
          :icon="stat.icon"
          :value="stat.value"
          :color="stat.color"
        >
          <template
            v-if="(stat.establishments?.length ?? 0) > 0"
            #title-tooltip
          >
            <ul class="connector-infos-establishments">
              <li v-for="esta in stat.establishments">
                <span class="connector-infos-establishments-name">{{ esta.name }}</span>
                <span
                  v-if="esta.details"
                  class="connector-infos-establishments-details"
                >
                  ({{ esta.details }})
                </span>
              </li>
            </ul>
          </template>
        </connector-info-card>
      </b-col>
    </b-row>
    <hr />
  </div>
</template>

<script>
import ConnectorInfoCard from './ConnectorInfoCard.vue';

import {
  getAwaitedReports,
  getAwaitedTransmissions,
  getCrashedEstablishments,
} from "../services/exchange";

export default {
  components: {
    ConnectorInfoCard
  },
  props: {
    cntId: {
      type: Number,
      required: false,
    },
    cntPushId: {
      type: Number,
      required: false,
    },
    establishments: {
      type: Array,
      default: [],
    },
    requirePushId: {
      type: Boolean,
      default: false,
    }
  },
  data() {
    return {
      loading: false,
      infos: [],
    };
  },
  watch: {
    cntId() {
      this.initConnectorStat();
    },
    cntPushId() {
      this.initConnectorStat();
    },
  },
  created() {
    this.initConnectorStat();
  },
  methods: {
    async initConnectorStat() {
      this.loading = true;

      if (this.cntId && (!this.requirePushId || this.cntPushId)) {
        const [
          reportData,
          transmissionData,
          crashData,
        ] = await Promise.all([
          getAwaitedReports(this.cntId, this.cntPushId ?? this.cntId),
          getAwaitedTransmissions(this.cntId, this.cntPushId ?? this.cntId),
          getCrashedEstablishments(this.cntId),
        ]);

        let distinctEstablishments = reportData?.reduce((list, data) => {
          const estaRow = list.find(row => row.id === data.rep_esta_id);

          if (estaRow) {
            estaRow.report_number++;
          }
          else {
            list.push({
              id: data.rep_esta_id,
              report_number: 1,
              transmission_number: 0,
            });
          }
          return list;
        }, []) ?? [];
        distinctEstablishments = transmissionData?.reduce((list, data) => {
          const estaRow = list.find(row => row.id === data.tra_esta_id);

          if (estaRow) {
            estaRow.transmission_number++;
          }
          else {
            list.push({
              id: data.tra_esta_id,
              report_number: 0,
              transmission_number: 1,
            });
          }
          return list;
        }, distinctEstablishments) ?? distinctEstablishments;

        this.infos = [
          {
            label: this.$t("connector.label.awaited_reports"),
            icon: "fa-regular fa-file-lines",
            color: "#3b82f6",
            value: reportData?.length ?? 0,
          },
          {
            label: this.$t("connector.label.awaited_transmissions"),
            icon: "fa-regular fa-comment-dots",
            color: "#dc8741",
            value: transmissionData?.length ?? 0
          },
          {
            label: this.$t("label.affected_establishments"),
            icon: "fa-regular fa-building",
            color: "#4eb47d",
            value: distinctEstablishments.length,
            establishments: distinctEstablishments
              .map(esta => {
                const estaDetailsList = [];

                if (esta.report_number > 0) {
                  estaDetailsList.push(this.$t("connector.label.nb_reports", [esta.report_number]));
                }
                if (esta.transmission_number > 0) {
                  estaDetailsList.push(this.$t("connector.label.nb_transmissions", [esta.transmission_number]));
                }

                return {
                  ...esta,

                  total_number: esta.report_number + esta.transmission_number,
                  name: this.establishments?.find(e => e.value === esta.id)?.text,
                  details: estaDetailsList.join(", "),
                };
              })
              .sort((a, b) => b.total_number - a.total_number),
          },
          {
            label: this.$t("connector.label.error"),
            icon: "fa-solid fa-triangle-exclamation",
            color: "#dc4141",
            value: crashData?.length ?? 0,
            establishments: (crashData ?? [])
              .map(c => {
                const daysSinceLastExec = (new Date() - new Date(c.eec_last_exec)) / (24 * 3600 * 1000);
                return {
                  ...c,

                  last_exec_interval: daysSinceLastExec,
                  name: `${c.esta_name} (${c.esta_id})`,
                  details: `${Math.floor(daysSinceLastExec)} ${this.$t("label.days")}`,
                }
              })
              .sort((a, b) => b.last_exec_interval - a.last_exec_interval),
          },
        ];
      }
      else {
        this.infos = [];
      }

      this.loading = false;
    },
  }
};
</script>

<style scoped >
.connector-infos-establishments {
  padding-left: 1em;
  text-align: left;
}
.connector-infos-establishments-name {
  font-weight: 500;
}
.connector-infos-establishments-details {
  font-size: 0.8em;
  font-style: italic;
  color: lightgray;
}
</style>

<style>
.connector-info-card-content-tooltip .tooltip-inner {
  max-width: 400px;
  max-height: 600px;
  overflow: auto;
}
</style>
